import React, { useEffect, useState, useCallback } from 'react'
import MaterialTable from 'material-table'
import {
  Dialog, DialogTitle, Snackbar, Paper, Typography, TextField, InputAdornment,
  IconButton, Tooltip, CircularProgress
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ClearIcon from '@material-ui/icons/Clear'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import SearchIcon from '@material-ui/icons/Search'
import Alert from '@material-ui/lab/Alert'
import ClientForm from './ClientForm'
import * as api from '../../api/clients'
import { localization } from '../../config'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1400,
    margin: '0 auto',
    fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  titleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  titleIcon: {
    color: '#1a73e8',
    fontSize: 28,
  },
  title: {
    fontWeight: 400,
    fontSize: '1.5rem',
    color: '#202124',
    margin: 0,
  },
  subtitle: {
    color: '#5f6368',
    fontSize: '0.8rem',
    margin: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flex: '1 1 420px',
    justifyContent: 'flex-end',
  },
  searchField: {
    width: 280,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      flex: '1 1 100%',
    },
  },
  iconBtn: {
    border: '1px solid #dadce0',
    borderRadius: 8,
    padding: 7,
    '&:hover': { backgroundColor: '#f1f3f4' },
  },
  addBtn: {
    backgroundColor: '#1a73e8',
    color: '#fff',
    borderRadius: 8,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    border: 'none',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#1557b0' },
    '&:disabled': { backgroundColor: '#dadce0', color: '#5f6368', cursor: 'default' },
  },
  paper: {
    borderRadius: 8,
    border: '1px solid #dadce0',
    overflow: 'hidden',
    boxShadow: '0 1px 2px 0 rgba(60,64,67,.3)',
  },
  dialogTitle: {
    borderBottom: '1px solid #e8eaed',
    padding: '16px 24px 12px',
    '& h2': { fontSize: '1.1rem', fontWeight: 500, color: '#202124' },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
}))

export default function ClientManager() {
  const classes = useStyles()
  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(false)
  const [tvas, setTvas]       = useState([])
  const [tvasLoading, setTvasLoading] = useState(false)
  const [tvasError, setTvasError] = useState('')
  const [search, setSearch]   = useState('')
  const [open, setOpen]       = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving]   = useState(false)
  const [toast, setToast]     = useState({ open: false, message: '', severity: 'success' })

  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getClients()
      if (res.success) setRows(res.data)
      else showToast(res.error || 'Erreur de chargement', 'error')
    } catch {
      showToast('Impossible de contacter le serveur', 'error')
    }
    setLoading(false)
  }, [])

  const loadTVAs = useCallback(async () => {
    setTvasLoading(true)
    setTvasError('')
    try {
      setTvas(await api.getTVAs())
    } catch {
      setTvasError('Impossible de charger les taux de TVA.')
      showToast('Impossible de charger les taux de TVA', 'error')
    } finally {
      setTvasLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    loadTVAs()
  }, [load, loadTVAs])

  const normalizedSearch = search.trim().toLocaleLowerCase('fr')
  const filteredRows = normalizedSearch
    ? rows.filter((row) =>
        [row.nom, row.ville, row.cp, row.telephone].some((value) =>
          String(value ?? '').toLocaleLowerCase('fr').includes(normalizedSearch)
        )
      )
    : rows

  const handleAdd   = () => { setEditing(null); setOpen(true) }
  const handleEdit  = (row) => { setEditing(row); setOpen(true) }
  const handleClose = () => { if (!saving) setOpen(false) }

  const handleSave = async (data) => {
    setSaving(true)
    try {
      if (editing?.id) {
        await api.updateClient(editing.id, data)
        showToast('Client mis à jour')
      } else {
        await api.createClient(data)
        showToast('Client créé')
      }
      setOpen(false)
      load()
    } catch (e) {
      showToast(e?.error || "Erreur lors de l'enregistrement", 'error')
    }
    setSaving(false)
  }

  return (
    <div className={classes.root}>
      {/* En-tête */}
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <PeopleOutlineIcon className={classes.titleIcon} />
          <div>
            <Typography variant="h6" className={classes.title}>Clients</Typography>
            <Typography className={classes.subtitle}>
              {normalizedSearch
                ? `${filteredRows.length} résultat${filteredRows.length !== 1 ? 's' : ''} sur ${rows.length} agences`
                : `${rows.length} agence${rows.length !== 1 ? 's' : ''}`}
            </Typography>
          </div>
        </div>
        <div className={classes.actions}>
          <TextField
            className={classes.searchField}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un client"
            variant="outlined"
            size="small"
            aria-label="Rechercher par nom, ville, code postal ou téléphone"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Effacer la recherche"
                    onClick={() => setSearch('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip title="Actualiser">
            <span>
              <IconButton size="small" className={classes.iconBtn} onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <button className={classes.addBtn} onClick={handleAdd}>
            <AddIcon style={{ fontSize: 18 }} />
            Nouveau client
          </button>
        </div>
      </div>

      {/* Table */}
      <Paper className={classes.paper} elevation={0}>
        <MaterialTable
          title=""
          isLoading={loading}
          columns={[
            {
              title: 'Nom',
              field: 'nom',
              defaultSort: 'asc',
              render: (row) => (
                <Typography variant="body2" style={{ fontWeight: 500, color: '#202124' }}>
                  {row.nom || '—'}
                </Typography>
              ),
            },
            { title: 'Ville', field: 'ville' },
            { title: 'Code postal', field: 'cp', width: 110 },
            { title: 'Téléphone', field: 'telephone' },
            {
              title: 'Échéance N',
              field: 'echeance_n',
              width: 120,
              render: (row) => `${row.echeance_n ?? 60} j`,
            },
            { title: 'Coefficient', field: 'coef', width: 110 },
          ]}
          data={filteredRows}
          options={{
            pageSize: 15,
            pageSizeOptions: [15, 30, 50, 100],
            actionsColumnIndex: -1,
            search: false,
            toolbar: false,
            elevation: 0,
            headerStyle: {
              fontWeight: 600,
              fontSize: 12,
              color: '#5f6368',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e8eaed',
            },
            rowStyle: {
              fontSize: 13,
              color: '#202124',
              height: 56,
            },
          }}
          actions={[
            {
              icon: () => <EditIcon fontSize="small" style={{ color: '#5f6368' }} />,
              tooltip: 'Modifier',
              onClick: (_, row) => handleEdit(row),
            },
          ]}
          localization={{
            ...localization,
            body: {
              ...localization.body,
              emptyDataSourceMessage: rows.length === 0
                ? 'Aucun client enregistré.'
                : 'Aucun client ne correspond à votre recherche.',
            },
          }}
        />
      </Paper>

      {/* Dialog formulaire */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        disableBackdropClick={saving}
      >
        <DialogTitle className={classes.dialogTitle}>
          {editing ? `Modifier — ${editing.nom}` : 'Nouveau client'}
        </DialogTitle>
        <ClientForm
          initial={editing || {}}
          agences={rows}
          tvas={tvas}
          tvasLoading={tvasLoading}
          tvasError={tvasError}
          onRetryTVAs={loadTVAs}
          onCancel={handleClose}
          onSave={handleSave}
          saving={saving}
        />
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert elevation={6} variant="filled" severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  )
}
